import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { theme } from '../../styles/theme';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import CollapsibleComparePackages from './components/CollapsibleComparePackages';
import ScrollToTop from '../common/ScrollToTop';
import AccountStepper from './shared/AccountStepper';
import { WebsiteMarketingCtaButton } from '../common/websiteSection';
import PricingCard from '../pricing/PricingCard';
import PricingBillingToggle from '../pricing/PricingBillingToggle';
import {
  pricingPackageLimits,
  pricingSectionCopy,
} from '../pricing/pricingData';
import { usePublicPackages } from '../../hooks/usePublicPackages';
import { isContactSalesPackage } from '../../utils/contactSalesPackage';
import {
  PricingSectionShell,
  PricingSectionContainer,
  NoHiddenFeesMessage,
  PricingGrid,
} from '../pricing/pricingSectionStyles';
import { featureFlags } from '../../config/featureFlags';

const TotalSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${theme.spacing['2xl']};
  flex-wrap: wrap;
  gap: ${theme.spacing.md};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TotalPrice = styled.p`
  margin: 0;
  font-size: ${theme.typography.fontSize.xl};
  font-weight: 700;
  color: var(--login-form-text);

  span {
    color: var(--login-primary);
  }

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const NextButton = styled(WebsiteMarketingCtaButton)`
  border-radius: 999px;
  padding: 14px 28px;
  box-shadow: none;
  background: var(--login-primary);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    transform: none;
    box-shadow: none;
    background: var(--login-primary-dark);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const TrialBanner = styled.div`
  background: rgba(72, 138, 236, 0.08);
  border: 1px solid rgba(72, 138, 236, 0.18);
  border-radius: 20px;
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.lg};

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const TrialText = styled.div`
  h3 {
    margin: 0 0 ${theme.spacing.xs};
    font-size: ${theme.typography.fontSize.lg};
    font-weight: 700;
    color: var(--login-form-text);
  }

  p {
    margin: 0;
    color: var(--login-form-muted);
    line-height: 1.55;
  }
`;

const TrialButton = styled(NextButton)`
  flex-shrink: 0;
`;

const PackageSelection = ({ onNext }) => {
  const location = useLocation();
  const initialFromState = location.state?.selectedPackageId || null;
  const initialPackage = featureFlags.FREE_TRIAL_ENABLED
    ? initialFromState
    : initialFromState === 'free_trial'
      ? null
      : initialFromState;
  const [selectedPackage, setSelectedPackage] = useState(initialPackage);
  const [isYearly, setIsYearly] = useState(false);
  const { packages: pricingPackages } = usePublicPackages();

  const isPreSelectedFreeTrial =
    featureFlags.FREE_TRIAL_ENABLED && location.state?.selectedPackageId === 'free_trial';

  useEffect(() => {
    if (!featureFlags.FREE_TRIAL_ENABLED && selectedPackage === 'free_trial') {
      setSelectedPackage(null);
    }
    if (selectedPackage && pricingPackages.some(
      (pkg) => pkg.id === selectedPackage && isContactSalesPackage(pkg)
    )) {
      setSelectedPackage(null);
    }
  }, [selectedPackage, pricingPackages]);

  const calculateTotal = () => {
    if (!selectedPackage || selectedPackage === 'free_trial') return 0;

    const selectedPkg = pricingPackages.find((pkg) => pkg.id === selectedPackage);
    if (!selectedPkg) return 0;

    return isYearly ? selectedPkg.yearlyPrice : selectedPkg.monthlyPrice;
  };

  const handleContinue = () => {
    if (!selectedPackage) return;

    const selectedPkg = pricingPackages.find((pkg) => pkg.id === selectedPackage);
    const limits = selectedPkg?.limits || pricingPackageLimits[selectedPackage] || pricingPackageLimits.business;

    onNext?.({
      package: selectedPackage,
      addons: {},
      total: calculateTotal(),
      billingCycle: isYearly ? 'yearly' : 'monthly',
      interval: isYearly ? 'year' : 'month',
      isYearly,
      packageDetails: {
        id: selectedPkg?.firestoreId || selectedPackage,
        name: selectedPkg?.name,
        addons: {},
        limits,
      },
    });
  };

  const billingCycle = isYearly ? 'yearly' : 'monthly';

  return (
    <PricingSectionShell>
      <ScrollToTop />
      <PricingSectionContainer>
        <AccountStepper currentStep={1} />

        {featureFlags.FREE_TRIAL_ENABLED && (
          <TrialBanner>
            <TrialText>
              <h3>
                {isPreSelectedFreeTrial ? 'Free Trial Selected' : 'Start Your 14-Day Free Trial'}
              </h3>
              <p>
                {isPreSelectedFreeTrial
                  ? "You're about to start your 14-day free trial with all Enterprise features included. No credit card required."
                  : 'Try our Enterprise package with all features included. No credit card required.'}
              </p>
            </TrialText>
            <TrialButton
              type="button"
              onClick={() =>
                onNext?.({
                  package: 'free_trial',
                  addons: {},
                  total: 0,
                  billingCycle: 'monthly',
                  interval: 'month',
                  isYearly: false,
                })
              }
            >
              {isPreSelectedFreeTrial ? 'Continue with Free Trial' : 'Start Free Trial'}
            </TrialButton>
          </TrialBanner>
        )}

        <NoHiddenFeesMessage>
          <span>{pricingSectionCopy.noHiddenFees}</span> {pricingSectionCopy.noHiddenFeesDetail}
        </NoHiddenFeesMessage>

        <PricingBillingToggle isYearly={isYearly} onChange={setIsYearly} />

        <PricingGrid>
          {pricingPackages.map((pkg) => (
            <PricingCard
              key={pkg.id}
              package={pkg}
              billingCycle={billingCycle}
              selectable={!isContactSalesPackage(pkg)}
              isSelected={selectedPackage === pkg.id}
              onSelect={setSelectedPackage}
              actionLabel={isContactSalesPackage(pkg) ? 'Contact Us' : 'Get Started'}
            />
          ))}
        </PricingGrid>

        <CollapsibleComparePackages selectedPackage={selectedPackage} />

        <TotalSection>
          <TotalPrice>
            Total: <span>${calculateTotal().toFixed(2)}</span>/{isYearly ? 'year' : 'month'}
          </TotalPrice>
          <NextButton type="button" disabled={!selectedPackage} onClick={handleContinue}>
            Next Step
            <ArrowForwardIcon />
          </NextButton>
        </TotalSection>
      </PricingSectionContainer>
    </PricingSectionShell>
  );
};

export default PackageSelection;
